import { AfterViewInit, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, ParamMap, RouterLink } from '@angular/router';
import { catchError, debounceTime, finalize, of, tap, throwError } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { DndDraggableDirective, DndDropzoneDirective, DndHandleDirective } from 'ngx-drag-drop';
import { FilterConfig, GenAiModel, ListFilter, TenantConfiguration } from '@parashift/shared/models';
import { JsonApiQueryData, LexorankService, moveRankedItem, rankMoveFailedNotification, ListFilterService, Logger, ErrorResponse, NotificationService, GenAiModelService, GenAiModelsRegister, TenantConfigurationService } from '@parashift/shared/services';
import { LeftColumnComponent, FilterColumnComponent, ListPaginationComponent, ListDataWithTooltipComponent, FilterSortingComponent, FilterTagsComponent, ModalService } from '@parashift/shared/components';
import { HeaderActionStore } from '@parashift/shared/stores';
import { DraggableRow } from '../automation-rules/automation-rules-list.component';

@Component({
  selector: 'gen-ai-models-list',
  templateUrl: './gen-ai-models-list.component.html',
  styleUrls: ['./gen-ai-models-list.component.scss'],
  imports: [
    BsDropdownModule,
    DndDraggableDirective,
    DndDropzoneDirective,
    DndHandleDirective,
    FaIconComponent,
    FilterColumnComponent,
    FilterSortingComponent,
    FilterTagsComponent,
    FormsModule,
    LeftColumnComponent,
    ListDataWithTooltipComponent,
    ListPaginationComponent,
    NgTemplateOutlet,
    RouterLink,
    TooltipModule
  ]
})
export class GenAiModelsListComponent implements AfterViewInit, OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private genAiModelService = inject(GenAiModelService);
  private genAiModelsRegister = inject(GenAiModelsRegister);
  private tenantConfigurationService = inject(TenantConfigurationService);
  private listFilterService = inject(ListFilterService);
  private lexorankService = inject(LexorankService);
  private notificationService = inject(NotificationService);
  private modalService = inject(ModalService);
  private headerActionStore = inject(HeaderActionStore);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(FilterColumnComponent) filterComponent: FilterColumnComponent;
  @Input() filterColumnConfig: FilterConfig;

  genAiModels: JsonApiQueryData<GenAiModel>;
  listFilter: ListFilter;
  tenant_id: number;
  loading = true;
  disabled = signal(false);
  genAiModelIsBusy = '0';
  draggedRowIndex = undefined;
  allowDragging = false;
  allowMoveByClick = true;
  loadingAfterDragging = false;
  isFirstPage = true;
  isLastPage = false;
  defaultTenantTooltip = $localize `:@@components.gen_ai_models_list.default_tenant_tooltip:This is your tenant-wide default model`;
  nonDefaultTenantTooltip = $localize `:@@components.gen_ai_models_list.non_default_tenant_tooltip:Set this model as tenant-wide default model`;
  notLiveNonDefaultTenantTooltip = $localize `:@@components.gen_ai_models_list.not_live_non_default_tenant_tooltip:You can set this model as tenant-wide default model only if it has been set live`;

  private visibilityLabels: Record<string, string> = {
    tenant: $localize `:@@common.tenant:Tenant`,
    sub_tenants: $localize `:@@common.sub_tenants:Sub-Tenants`,
    public: $localize `:@@common.public:Public`,
  };

  visibilityTooltip(visibility: string): string {
    const label = this.visibilityLabels[visibility] ?? visibility;
    return $localize `:@@components.gen_ai_models_list.shared_with:shared with: ${label}:label:`;
  }

  private tenantConfiguration: TenantConfiguration;

  constructor() {
    const destroyRef = this.destroyRef;
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
      this.getTenantConfiguration();
      this.initListFilter();
      this.getGenAiModels();

      this.headerActionStore.addActions({
        newButton: { dispatch: () => this.addNewGenAiModel(), label: $localize `:@@components.gen_ai_models_list.add_model:Add model` }
      });
    });
  }

  initListFilter() {
    this.listFilter = this.listFilterService.getStoredFilters(
      this.filterColumnConfig.meta.identifier,
      {
        stats: { total: ['count'] }
      },
      true
    );
  }

  ngAfterViewInit() {
    this.filterComponent.$filterParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400)
      )
      .subscribe(listFilter => {
        this.listFilter = listFilter as ListFilter;
        this.allowDragging = false;
        this.setPageStates();
        this.getGenAiModels();
      });
  }

  getGenAiModels() {
    this.loading = true;
    this.allowDragging = false;
    this.genAiModelService.findAll(this.listFilter, this.tenant_id)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (genAiModels: JsonApiQueryData<GenAiModel>) => {
          this.genAiModels = genAiModels;
          this.setPageStates();
          this.isDraggingAllowed();
        },
        error: (error: ErrorResponse) => {
          this.allowDragging = false;
          this.logger.log('gen-ai-models-list-component', 'GEN AI MODELS Error', error);
        }
      });
  }

  moveModelUp(model: GenAiModel, index: number) {
    if (!this.allowMoveByClick) { this.allowMoveByClick = true; return; }
    this.moveModel(index, index - 1).subscribe();
  }

  moveModelDown(model: GenAiModel, index: number) {
    if (!this.allowMoveByClick) { this.allowMoveByClick = true; return; }
    this.moveModel(index, index + 1).subscribe();
  }

  onRowDragStart(rowIndex: number) {
    this.draggedRowIndex = rowIndex;
    this.allowMoveByClick = false;
  }

  onRowDragEnd() {
    this.draggedRowIndex = undefined;
    this.removeDraggedOverClass();
    this.allowMoveByClick = true;
  }

  onRowDrop(row: DraggableRow, dropzoneRow: DraggableRow) {
    this.moveModel(row.index, dropzoneRow.index).subscribe();
  }

  editGenAiModel(model: GenAiModel, event: MouseEvent) {
    if (!this.shouldOpenInNewTab(event) && model.tenant_id) {
      if (!model.tenant_id || model.tenant_id !== this.tenant_id) {
        this.router.navigate([model.id, 'detail'], { relativeTo: this.route });
      } else {
        this.router.navigate([model.id, 'edit'], { relativeTo: this.route });
      }
    }
  }

  deleteGenAiModel(model: GenAiModel) {
    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@components.gen_ai_models.delete_gen_ai_model.title:Delete GenAI Model`,
        description: $localize `:@@components.gen_ai_models.delete_gen_ai_model.description:Do you want to delete this GenAI Model?`,
        button_confirm: $localize `:@@components.gen_ai_models.delete_gen_ai_model.button_confirm:Delete`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.genAiModelService.deleteRecord(model.id, this.tenant_id).subscribe({
        next: () => {
          this.notificationService.success({
            description: $localize `:@@components.gen_ai_models.delete_gen_ai_model.success_description:GenAI Model has been successfully deleted.`,
            title: $localize `:@@components.gen_ai_models.delete_gen_ai_model.success_title:GenAI Model deleted`
          });
          this.getGenAiModels();
          this.genAiModelsRegister.removeModel(model);
        },
        error: (error: ErrorResponse) => this.notificationService.formattedErrorResponse(error)
      });
    });
  }

  setTenantDefaultModel(model: GenAiModel) {
    this.disabled.set(true);
    this.tenantConfiguration.gen_ai_model_id = Number(model.id);
    this.tenantConfigurationService.saveRecord(this.tenantConfiguration, this.tenant_id).subscribe({
      next: (configuration: TenantConfiguration) => {
        this.tenantConfiguration = configuration;
        this.genAiModelsRegister.setTenantDefaultModelId(model.id);
        this.getGenAiModels();
        this.notificationService.success({
          description: $localize `:@@components.gen_ai_models.configuration_saved_description:The model has been succesfully saved as tenant-wide default model.`,
          title: $localize `:@@components.gen_ai_models.configuration_saved_title:Successfully saved`
        });
        this.disabled.set(false);
      },
      error: (error: ErrorResponse) => {
        this.disabled.set(false);
        this.logger.log('Save Configuration Error', error);
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }

  setLive(live: boolean, genAiModel: GenAiModel) {
    const oldLive = genAiModel.live;
    genAiModel.live = live;

    if (live) {
      this.modalService.confirmation({
        initialState: {
          emitCancelEvent: true,
          description: $localize `:@@components.gen_ai_models_list.set_live.modal.description:We will set this GenAI Model to live.`,
          title: $localize `:@@components.gen_ai_models_list.set_live.modal.title:Set to live`
        }
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.setLiveAndSave(genAiModel, oldLive, live, {
            title: $localize `:@@components.gen_ai_models_list.set_live_and_save.success.title:Succesfully set to live`,
            description: $localize `:@@components.gen_ai_models_list.set_live_and_save.success.description:The GenAI Model <strong>${ genAiModel.title || '#' + genAiModel.id }</strong> has been successfully set to live`
          });
        } else {
          genAiModel.live = oldLive;
        }
      });
    } else {
      this.setLiveAndSave(genAiModel, oldLive, live, {
        title: $localize `:@@components.gen_ai_models_list.set_live.success.not_live.title:Successfully removed from live`,
        description: $localize `:@@components.gen_ai_models_list.set_live.success.not_live.description:The GenAI Model <strong>${ genAiModel.title || '#' + genAiModel.id }:genAiModel:</strong> has been successfully removed from live`
      });
      if (genAiModel.tenant_default) {
        this.removeTenantDefaultModel();
      }
    }
  }

  private setLiveAndSave(genAiModel: GenAiModel, oldLive: boolean, live: boolean, notification: { title: string, description: string }) {
    this.genAiModelIsBusy = genAiModel.id;

    genAiModel.live = live;

    this.genAiModelService.saveRecord(genAiModel, this.tenant_id).subscribe({
      next: (result: GenAiModel) => {
        this.replaceUpdatedModel(genAiModel, result);
        this.genAiModelIsBusy = '0';
        this.notificationService.success(notification);
        genAiModel.live && this.genAiModelsRegister.addOrReplaceModel(genAiModel);
        !genAiModel.live && this.genAiModelsRegister.removeModel(genAiModel);
      },
      error: (error: ErrorResponse) => {
        genAiModel.live = oldLive;
        this.genAiModelIsBusy = '0';
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }

  private setPageStates() {
    this.isFirstPage = this.listFilter.page.number === 1;
    const lastPage = Math.ceil(this.genAiModels?.getMeta().meta.stats.total.count / this.listFilter.page.size);
    this.isLastPage = this.listFilter.page.number === lastPage;
  }

  private isDraggingAllowed() {
    this.loadingAfterDragging = false;
    this.allowDragging = this.listFilter.sorting?.field === 'rank';
  }

  private moveModel(from: number, to: number) {
    if (!this.allowDragging || this.loadingAfterDragging) { return of(null); }
    this.loadingAfterDragging = true;
    this.allowMoveByClick = false;

    return moveRankedItem(this.lexorankService, {
      list: this.genAiModels,
      listFilter: this.listFilter,
      from,
      to,
      loadPage: listFilter => this.genAiModelService.findAll(listFilter, this.tenant_id),
      save: model => this.saveGenAiModel(model)
    }).pipe(
      tap(move => {
        this.allowMoveByClick = true;
        if (move?.leavesPage) {
          this.filterComponent?.setPageValue({ itemsPerPage: this.listFilter.page.size, page: this.listFilter.page.number + move.leavesPage });
        } else {
          this.loadingAfterDragging = false;
        }
      }),
      catchError((error: ErrorResponse | Error) => {
        this.loadingAfterDragging = false;
        this.allowMoveByClick = true;

        if (error instanceof Error) {
          // Invalid neighbour ranks: local list is out of sync with the backend, so reload it
          this.logger.log('gen-ai-models-list-component', 'Move failed', error);
          this.notificationService.error(rankMoveFailedNotification());
          this.getGenAiModels();
        } else {
          this.notificationService.formattedErrorResponse(error);
        }
        return throwError(() => error);
      })
    );
  }

  private saveGenAiModel(model: GenAiModel) {
    return this.genAiModelService.saveRecord(model, this.tenant_id);
  }

  private removeDraggedOverClass() {
    const draggedOverList = this.elementRef.nativeElement.querySelectorAll('.dndDragover');
    draggedOverList.forEach(el => el.classList.remove('dndDragover'));
  }

  private getTenantConfiguration() {
    this.tenantConfigurationService.findAll(undefined, this.tenant_id).subscribe({
      next: (configurations: JsonApiQueryData<TenantConfiguration>) => {
        this.tenantConfiguration = configurations.getModels()[0];
      },
      error: (error: ErrorResponse) => {
        this.logger.log('gen-ai-models-list-component', 'TENANT CONFIGURATION Error', error);
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }

  private removeTenantDefaultModel() {
    this.disabled.set(true);
    this.tenantConfiguration.gen_ai_model_id = null;
    this.tenantConfigurationService.saveRecord(this.tenantConfiguration, this.tenant_id).subscribe({
      next: (configuration: TenantConfiguration) => {
        this.tenantConfiguration = configuration;
        this.genAiModelsRegister.setTenantDefaultModelId(undefined);
        this.getGenAiModels();
        this.disabled.set(false);
      },
      error: (error: ErrorResponse) => {
        this.disabled.set(false);
        this.logger.log('Save Configuration Error', error);
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }

  private replaceUpdatedModel(oldModel: GenAiModel, newModel: GenAiModel) {
    this.genAiModels.replaceModel(oldModel.id, newModel);
  }

  private addNewGenAiModel() {
    this.router.navigate(['new', 'edit'], { relativeTo: this.route });
  }

  private shouldOpenInNewTab(event: MouseEvent) {
    return event.metaKey === true || event.ctrlKey === true; // metaKey = Mac, ctrlKey = Windows
  }
}
